import React, { useEffect, useState } from 'react';
import { buildClassName } from 'utils/build-class-name';
import ListingStatus from 'components/listing-status';
import Timestamp from './components/timestamp';
import ListingPrice from 'components/listing-price';
import SchemaOrg from 'components/schema-org';
import BathIcon from 'components/icon/bath-icon';
import BedIcon from 'components/icon/bed-icon';
import Button from 'components/control/button';
import SqftIcon from 'components/icon/sqft-icon';
import ClockIcon from 'components/icon/clock-icon';
import CameraIcon from 'components/icon/camera-icon';
import CarIcon from 'components/icon/car-icon';
import FireIcon from 'components/icon/fire-icon';
import SqftText from 'components/sqft-text';
import { CONNECTION_REQUEST_MOBILE_MODAL, CONNECTION_REQUEST_POPUP_MODAL, HOME_APPRAISAL_CONNECTION_REQUEST_MODAL, useModalContext } from 'contexts/modal';
import { useIsSmallMobile } from 'hooks/use-size-class';
import { useRouter } from 'next/router';
import AddressHeading from './components/address-heading';
import ListingPagePrice from './components/price';
import ListingOpenHouses from './components/open-houses';
import { listingPageModalIds } from 'constants/test-constants';
import styles from './style.module.scss';
import { TryScrambleText } from 'utils/text-scrambler';
import { useExperiment } from '@growthbook/growthbook-react';
import { GTM_CLICK_ADDRESS_PAGE_MAIN_PVR_CTA, GTM_CLICK_ADDRESS_PAGE_PRICE_HISTORY_BUTTON,
  GTM_CLICK_ADDRESS_PAGE_VIRTUAL_TOUR_BUTTON, GTM_CLICK_LISTING_MODAL_ADDRESS_TEXT } from 'constants/events';
import Cookies from 'js-cookie';
import { FIRST_VISIT_SESSION_DECISION_KEY, FIRST_VISIT_TIMESTAMP_KEY, MLML_AGENT_COOKIE_NAME } from 'constants/cookies';
import MyLinkMyLeadCreate from 'components/my-link-my-lead-create';
import { ExperimentVariants } from 'utils/types';

import { createProvidersComplianceApi } from 'data/providers-compliance';
import FavouriteToggleV2 from 'components/pages/address/public/components/listing-content/components/favourite-toggle';
import { getTenantById, getThemeByTenantId } from '@tenants';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { type TFunction, useTranslation } from 'next-i18next';
import { AnalyticsController } from 'analytics/analytics-controller';
import { ComplianceRequirement } from 'types/compliance';
import { useQuery } from '@tanstack/react-query';
import { DISCLAIMER_INFO_QUERY_KEY } from 'constants/use-query';
import { CourtesyOfListingData } from 'data/providers-compliance/providers.api';
import { useTrackExperimentVariant } from 'hooks/use-track-experiment-variant';
import { useUserContext } from 'contexts';
import { StandardStatus } from '@zoocasa/go-search';
import { isSold } from 'types/reso';
import storage from 'utils/storage';
import { TimestampPresentation } from 'domain/price';
import { useListingsCompliance } from 'hooks/use-listings-compliance';
import { formatMlsNumber } from 'ui/mls-info/format-mls-info';

import type Listing from 'data/listing';
import type { Zoopraisal } from 'data/zoopraisal';
import type { PropsWithTenantId } from '@tenants/types';

//#region Types
type ExperimentProps = {
  soldListingExperimentId?: ExperimentVariants | null;
};

type ListingContentProps = PropsWithTenantId<{
  listing: Listing;
  complianceRequirement: ComplianceRequirement;
  isModal?: boolean;
  priceHistoryRef: React.RefObject<HTMLDivElement>;
  zoopraisal?: Zoopraisal | null;
  showPriceHistoryBtn?: boolean;
  showsInfoIcon?: boolean;
  isFromPriceHistory?: boolean;
  shouldScramble?: boolean;
  showFavouriteToggle?: boolean;

  addressPath?: string;
  address: string;
  propertyType: string;
  listingSource?: string;
}> & ExperimentProps;
//#endregion

//#region Internal Components

function mlsNumber(mlsNum: string, providerId?: number | null) {
  return (
    <p className={styles['mls-number']}>{formatMlsNumber({ mlsNumber: mlsNum, providerId })}</p>
  );
}

function MlsCourtesyRow({
  mlsNum,
  providerId,
  courtesyText,
  representation,
}: {
  mlsNum: string;
  providerId?: number | null;
  courtesyText?: string;
  representation: string | null;
}) {
  return (
    <>
      <div className={styles['mls-courtesy-row']} data-testid="listing-mls-courtesy-row">
        {mlsNumber(mlsNum, providerId)}
        {courtesyText ?
          <p className={styles.courtesy}>{courtesyText}</p>
          : null}
      </div>
      {representation ?
        <p className={styles.courtesy}>{representation}</p>
        : null}
    </>
  );
}

function ContactforPriceMobile({ timestamp, standardStatus, isRental, onContactForPriceClick }: { timestamp: Date | null; standardStatus: StandardStatus; isRental: boolean; onContactForPriceClick: () => void }) {
  const { t } = useTranslation(Namespaces.COMMON);
  return (
    <>
      <Timestamp timestamp={timestamp} status={standardStatus} isRental={isRental} className={styles['listed-since']} />
      <span className={styles['contact-for-price']} onClick={onContactForPriceClick} >
        {t(LOCALE_COMMON_PATHS.LISTING_CONTENT_CONTACT_FOR_PRICE, { defaultValue: 'Contact for Price' })}
      </span>
    </>
  );
}

function PriceAndTimestampMobile({ timestamp, standardStatus, isRental, listing, complianceRequirement }: { timestamp: Date | null; standardStatus: StandardStatus; isRental: boolean; listing: Listing; complianceRequirement: ComplianceRequirement }) {
  return (
    <>
      <Timestamp timestamp={timestamp} status={standardStatus} isRental={isRental} className={styles['listed-since']} />
      <ListingPagePrice listing={listing} isVisible={complianceRequirement === ComplianceRequirement.NONE} />
    </>
  );
}

function ContactforPriceDesktop({ timestamp, standardStatus, isRental, onContactForPriceClick }: { timestamp: Date | null; standardStatus: StandardStatus; isRental: boolean; onContactForPriceClick: () => void }) {
  const { t } = useTranslation(Namespaces.COMMON);
  return (
    <>
      <span className={styles['contact-for-price']} onClick={onContactForPriceClick} >
        {t(LOCALE_COMMON_PATHS.LISTING_CONTENT_CONTACT_FOR_PRICE, { defaultValue: 'Contact for Price' })}
      </span>
      <Timestamp timestamp={timestamp} status={standardStatus} isRental={isRental} className={styles['listed-since']} />
    </>
  );
}

function PriceAndTimestampDesktop({ timestamp, standardStatus, isRental, listing, complianceRequirement, showsInfoIcon, shouldScramble }: { timestamp: Date | null; standardStatus: StandardStatus; isRental: boolean; listing: Listing; complianceRequirement: ComplianceRequirement; showsInfoIcon: boolean; shouldScramble: boolean }) {
  return (
    <>
      <ListingPrice listing={listing} isVisible={complianceRequirement === ComplianceRequirement.NONE} testId={listingPageModalIds.listingPriceModal} shouldScramble={shouldScramble} showsInfoIcon={showsInfoIcon} />
      <Timestamp timestamp={timestamp} status={standardStatus} isRental={isRental} className={styles['listed-since']} testId={listingPageModalIds.modalListingTimeStamp} />
    </>
  );
}

//#endregion

//#region Main Component

export default function ListingContent(props: ListingContentProps) {
  const {
    tenantId,
    listing,
    isModal = false,
    priceHistoryRef,
    shouldScramble,
    zoopraisal,
    showsInfoIcon,
    soldListingExperimentId = undefined,
    showPriceHistoryBtn = false,
    showFavouriteToggle = true,
    complianceRequirement = ComplianceRequirement.NONE,
    addressPath,
    address,
    propertyType,
    listingSource,
  } = props;
  const {
    bedrooms,
    id,
    position,
    bathrooms,
    sqftLabel,
    parking,
    virtualTourUrl,
    openHouses,
    price,
    providerId,
    isAvailable,
    isRental,
    buyerInfo,
    daysOnMarket,
    standardStatus,
    brokerage,
    brokerageEmail,
    brokeragePhone,
    lastFetched,
    listingAgent,
    listingAgentContact,
    originalMlsName,
    province,
    mlsNum,
    canDisplaySoldPrice,
  } = listing;
  const { t } = useTranslation(Namespaces.COMMON);
  const router = useRouter();
  const tenant = getTenantById(tenantId);
  const { openModal } = useModalContext();
  const tenantTheme = getThemeByTenantId(tenantId);
  const isMobile = useIsSmallMobile();
  const timeRemainingEstimation: string | null = getEstimatedTimeRemainingOnMarket(zoopraisal, daysOnMarket, isAvailable, isRental, t);
  const { hasNavigatedFromInternalLink } = useUserContext();
  const isVisible = complianceRequirement === ComplianceRequirement.NONE;
  const listingsCompliance = useListingsCompliance([{ providerId, standardStatus }]);
  const hideSquareFootage = listingsCompliance?.[0]?.compliance?.hideSquareFootage;

  const timestampPresentation = TimestampPresentation.fromListing({
    standardStatus: listing.standardStatus,
    soldAt: listing.soldAt,
    updatedOn: listing.updatedOn,
    addedAt: listing.addedAt,
    displayPolicy: listing.displayPolicy,
  });
  const timestamp = listing.hideMarketTime ? null : timestampPresentation.timestamp;

  const buyerRepresentation = standardStatus && isSold(standardStatus)
    ? getRepresentedByAgent(buyerInfo?.agentFirstName, buyerInfo?.agentLastName, buyerInfo?.officeName, t)
    : null;
  const showsMLMLCreate = tenant.features.MyLinkMyLead && !!Cookies.get(MLML_AGENT_COOKIE_NAME);
  const isExperimentEnabled = !hasNavigatedFromInternalLink && isAvailable && isMobile;

  // variations/weights mirror the live GrowthBook config for 'mobile-web-pvr-buttons'
  // (3 variations; the 3rd currently carries 0% weight) - kept as an inline experiment
  // rather than gb.evalFeature() so the `active` eligibility gate below is preserved.
  const abcTestingPVRButtons = useExperiment({ key: 'mobile-web-pvr-buttons', variations: [0, 1, 2], weights: [0, 1, 0], active: isExperimentEnabled });

  // Deciding first-visit-vs-recurring needs localStorage/sessionStorage, which don't exist on the
  // server. Rendering must match on the client's hydration pass, so no storage read can happen
  // during render - default to "not first visit" (matches what the server rendered) and only flip
  // to true from a post-mount effect, once we're safely past hydration. This means a genuine
  // first-time visitor sees the CTA appear a beat after mount rather than in the initial paint,
  // but a returning visitor never sees it flash in and then vanish (nor a hydration mismatch).
  const [isFirstVisit, setIsFirstVisit] = useState(false);

  useEffect(() => {
    // Already decided earlier in this tab session - a refresh/reload must stay consistent with
    // that, not re-derive from storedTimestamp (which this same effect, on the earlier mount,
    // already bumped to "now" - re-deriving would misread every reload as "recurring").
    const sessionDecision = sessionStorage.getItem(FIRST_VISIT_SESSION_DECISION_KEY);
    let recurring: boolean;
    if (sessionDecision !== null) {
      recurring = sessionDecision === 'recurring';
    } else {
      const storedTimestamp = (storage.get(FIRST_VISIT_TIMESTAMP_KEY) as number) || null;
      const now = Date.now();
      if (!storedTimestamp) {
        recurring = false;
      } else {
        const timeDifferenceDays = (now - storedTimestamp) / (1000 * 60 * 60 * 24);
        recurring = isExperimentEnabled && timeDifferenceDays < 30;
      }
      if (!storedTimestamp || recurring) {
        storage.set(FIRST_VISIT_TIMESTAMP_KEY, now);
      }
      sessionStorage.setItem(FIRST_VISIT_SESSION_DECISION_KEY, recurring ? 'recurring' : 'first');
    }
    // One-time sync to the external storage-backed first-visit signal - the session-decision
    // pinning above makes this idempotent, not a cascading update.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsFirstVisit(!recurring);
  }, [isExperimentEnabled]);

  const { data: disclaimer, isLoading } = useQuery({
    queryKey: [DISCLAIMER_INFO_QUERY_KEY, providerId, tenantTheme.id, id],
    queryFn: () => {
      const providerCompliance = createProvidersComplianceApi();
      const courtesyOfListingData: CourtesyOfListingData = {
        brokerage: brokerage,
        brokerageEmail: brokerageEmail,
        brokeragePhone: brokeragePhone,
        lastFetched: lastFetched,
        listingAgent: listingAgent,
        listingAgentContact: listingAgentContact,
        originalMlsName: originalMlsName,
        province: province,
      };
      return providerCompliance.getDisclaimer(providerId.toString(), tenantTheme.id, courtesyOfListingData);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  useTrackExperimentVariant({
    variant: abcTestingPVRButtons.variationId,
    eventA: 'XpA_AddressStxBarView2',
    eventB: 'XpB_AddressStxBarView2',
    isActive: isExperimentEnabled && isMobile,
  });

  const openConnectionRequest = (isViewingRequest = false, eventName: string | null = null, customButtonText?: string, prefillData = {}) => {
    if (!eventName) {
      if (soldListingExperimentId === ExperimentVariants.A) AnalyticsController.trackEvent('XpA_AddressTakeATourButClk');
      AnalyticsController.trackEvent(GTM_CLICK_ADDRESS_PAGE_MAIN_PVR_CTA);
    }
    // Only track event if experiment event name is provided
    if (eventName ) {
      AnalyticsController.trackEvent(eventName);
      if (eventName !== 'XpA_AddressTourButClk2' &&
          eventName !== 'XpB_AddressTourButClk2' &&
          eventName !== 'XpB_AddressLearnMoreButClk2' &&
          eventName !== 'XpA_AddressLearnMoreButClk2') {
        AnalyticsController.trackEvent('XpAll_AddressButClk');
      }
    }
    openModal(CONNECTION_REQUEST_POPUP_MODAL, { listing, isViewingRequest, customButtonText, prefillData });
  };

  //alternate version of openConnectionRequest to use connection-request instead of connection-request-popup
  const openConnectionRequestMobile = () => {
    const isViewingRequest = true;
    AnalyticsController.trackEvent(GTM_CLICK_ADDRESS_PAGE_MAIN_PVR_CTA);

    openModal(CONNECTION_REQUEST_MOBILE_MODAL, { listing, isViewingRequest, customButtonText: 'Request Info', prefillData: { requestViewing: false, isShowingAdditionalComments: true, sourceComments: "I'd like to view the price of this property." }});
  };

  const handleSoldExperimentClick = () => {
    openModal(HOME_APPRAISAL_CONNECTION_REQUEST_MODAL, { listing });
  };

  const handleSoldSimilarListings = () => {
    AnalyticsController.trackEvent('XpC_AddressHomeEstButClk');
    openAreaPage();
  };

  const openAreaPage = () => {
    const parts = router.asPath.split('/');
    // City area page
    let url = '/' + parts[1];
    // We have neighborhood in the URL
    if (parts.length > 3) {
      url += '/' + parts[2];
    }
    router.push(url);
  };

  const scrollToPriceHistory = () => {
    if (priceHistoryRef.current) {
      priceHistoryRef.current.scrollIntoView({ behavior: 'smooth' });
      if (!isModal) {
        AnalyticsController.trackEvent(GTM_CLICK_ADDRESS_PAGE_PRICE_HISTORY_BUTTON);
      }
    }
  };

  const trackVirtualTourClick = () => {
    if (!isModal) {
      AnalyticsController.trackEvent(GTM_CLICK_ADDRESS_PAGE_VIRTUAL_TOUR_BUTTON);
    }
  };

  return (
    <div className={styles.component}>
      {isMobile &&
        <ListingStatus status={standardStatus} isRental={isRental} providerId={providerId} mlsStatus={listing.mlsStatus} className={buildClassName(styles['listing-status'], styles['mobile-status'], isModal && styles['modal-status'])} />
      }
      <div className={styles.row}>
        <div className={styles.left}>
          {(isMobile && canDisplaySoldPrice) &&
            <span className={buildClassName(styles['mobile-only'], styles['price-time-row'])}>
              {price === 0 ? (
                <ContactforPriceMobile timestamp={timestamp} standardStatus={standardStatus} isRental={isRental} onContactForPriceClick={openConnectionRequestMobile} />
              ) : (
                <PriceAndTimestampMobile timestamp={timestamp} standardStatus={standardStatus} isRental={isRental} listing={listing} complianceRequirement={complianceRequirement} />
              )}
            </span>
          }
          <SchemaOrg itemType="SingleFamilyResidence">
            <SchemaOrg itemProp="address" itemType="PostalAddress">
              <AddressHeading
                onAddressClick={() => AnalyticsController.trackEventConditionally({ event: GTM_CLICK_LISTING_MODAL_ADDRESS_TEXT }, isModal)}
                complianceRequirement={complianceRequirement}
                addressPath={addressPath}
                address={address}
                propertyType={propertyType}
                testId={listingPageModalIds.listingAddress}
                listingSource={listingSource}
              />
              {!isMobile &&
                <div className={styles['status-row']} data-testid={listingPageModalIds.modalListingStatus}>
                  <ListingStatus status={standardStatus} isRental={isRental} providerId={providerId} mlsStatus={listing.mlsStatus} />
                </div>
              }
            </SchemaOrg>
            <SchemaOrg itemProp="geo" itemType="GeoCoordinates">
              <meta itemProp="latitude" content={position ? position.latitude.toString() : ''} />
              <meta itemProp="longitude" content={position ? position.longitude.toString() : ''} />
            </SchemaOrg>
          </SchemaOrg>
        </div>
        <div className={styles.right} data-testid={listingPageModalIds.modalPriceInfoSection}>
          {!isMobile && (canDisplaySoldPrice ? (
            <span className={buildClassName(styles['desktop-listing-time'])}>
              {price === 0 ? (
                <ContactforPriceDesktop
                  timestamp={timestamp}
                  standardStatus={standardStatus}
                  isRental={isRental}
                  onContactForPriceClick={() => openConnectionRequest(true, null, 'Request Info', { requestViewing: false, isShowingAdditionalComments: true, sourceComments: "I'd like to view the price of this property." })} />
              ) : (
                <PriceAndTimestampDesktop
                  timestamp={timestamp}
                  standardStatus={standardStatus}
                  isRental={isRental}
                  listing={listing}
                  complianceRequirement={complianceRequirement}
                  showsInfoIcon={showsInfoIcon}
                  shouldScramble={shouldScramble} />)
              }
            </span>
          ) : '$---')}
          {timeRemainingEstimation &&
            <div className={styles['time-remaining']}>
              <span>
                <FireIcon />
                {t(LOCALE_COMMON_PATHS.LISTING_CONTENT_ESTIMATED_TO_SELL_IN, { defaultValue: 'Estimated to sell in:' })}
              </span>
              <span className={buildClassName((!isVisible) && 'blur', styles['red-text'])}>
                {timeRemainingEstimation}
              </span>
            </div>
          }
        </div>
        {showFavouriteToggle && (
          <div className={styles['favourite-toggle-wrapper']}>
            <FavouriteToggleV2 listingId={id} isInCarousel={false} isModal={isModal} isInline={true}/>
          </div>
        )}
      </div>
      <div className={`${styles.row} ${styles['aligned-row']}`}>
        <div className={buildClassName((!isVisible) && 'blur', styles['stats-container'])}>
          <span className={styles.stat} data-testid={listingPageModalIds.listingBedIcon}><BedIcon className={styles.bed} />{bedrooms}</span>
          <span className={styles.stat} data-testid={listingPageModalIds.listingBathrooms}><BathIcon className={styles.bath} />{bathrooms}</span>
          <span className={styles.stat} data-testid={listingPageModalIds.listingSqft}><SqftIcon className={styles.sqft} /><SqftText squareFootageLabel={hideSquareFootage ? '-' : TryScrambleText(sqftLabel, !isVisible) as string} render={formattedSqft => <>{formattedSqft}</>} /></span>
          <span className={styles.stat} data-testid={listingPageModalIds.listingCar}><CarIcon className={styles.car} />{parking || 0}</span>
        </div>

      </div>
      <div className={styles['price-virtual-tour-section']}>
        {showPriceHistoryBtn && <a onClick={scrollToPriceHistory} className={buildClassName(styles['button-wrapper'])}>
          <Button
            label={t(LOCALE_COMMON_PATHS.LISTING_CONTENT_PRICE_HISTORY_BUTTON_LABEL, { defaultValue: 'Price History' })}
            theme='secondary'
            Icon={ClockIcon}
            testId={listingPageModalIds.priceHistoryBtn} />
        </a>}
        {virtualTourUrl && true &&
          <a onClick={trackVirtualTourClick} href={virtualTourUrl} className={styles['button-wrapper']} target="_blank" rel="nofollow noreferrer">
            <Button
              label={t(LOCALE_COMMON_PATHS.LISTING_CONTENT_VIRTUAL_TOUR_BUTTON_LABEL, { defaultValue: 'Virtual Tour' })}
              theme='secondary'
              Icon={CameraIcon}
              testId={listingPageModalIds.virtualTourBtn} />
          </a>
        }
      </div>
      <MlsCourtesyRow
        mlsNum={mlsNum}
        providerId={listing.providerId}
        courtesyText={isLoading ? undefined : disclaimer?.CourtesyOf}
        representation={isLoading ? null : buyerRepresentation}
      />
      <ListingOpenHouses openHouses={openHouses} />

      {isMobile && showsMLMLCreate ?
        <div className={styles['pvr-cta-wrapper']}>
          <MyLinkMyLeadCreate buttonClassName={styles['pvr-cta-button']} listing={listing} />
        </div>
        :
        <>
          {/* Show experiment buttons for Mobile PVR */}
          {(isMobile && isExperimentEnabled && !hasNavigatedFromInternalLink && !soldListingExperimentId && isFirstVisit) && (
            <div className={styles['pvr-cta-wrapper']}>
              {abcTestingPVRButtons.variationId == ExperimentVariants.A && (
                <Button
                  label={t(LOCALE_COMMON_PATHS.LISTING_CONTENT_TAKE_TOUR_BUTTON_LABEL, { defaultValue: 'Take a Tour' })}
                  theme="primary"
                  className={styles['pvr-cta-button']}
                  onClick={() => {
                    openConnectionRequest(true, 'XpA_AddressTourButClk2');
                    AnalyticsController.trackEvent('XpAll_AddressButClk2');
                  }}
                  testId={listingPageModalIds.takeTourBtn}
                />
              )}
              {abcTestingPVRButtons.variationId == ExperimentVariants.B && (
                <>
                  <Button
                    label={t(LOCALE_COMMON_PATHS.LISTING_CONTENT_TAKE_TOUR_BUTTON_LABEL, { defaultValue: 'Take a Tour' })}
                    theme="primary"
                    className={styles['pvr-cta-button']}
                    onClick={() => {
                      openConnectionRequest(true, 'XpB_AddressTourButClk2');
                      AnalyticsController.trackEvent('XpAll_AddressButClk2');
                    }}
                    testId={listingPageModalIds.takeTourBtn}
                  />
                  <Button
                    label={t(LOCALE_COMMON_PATHS.LISTING_CONTENT_LEARN_MORE_BUTTON_LABEL, { defaultValue: 'Learn More' })}
                    theme="primary"
                    className={buildClassName(styles['pvr-cta-button'], styles.white)}
                    onClick={() => {
                      openConnectionRequest(false, 'XpB_AddressLearnMoreButClk2', 'Learn More');
                      AnalyticsController.trackEvent('XpAll_AddressButClk2');
                    }}
                  />
                </>
              )}
            </div>
          )}

          {/* Show default button */}
          {(isMobile && !isExperimentEnabled && soldListingExperimentId == ExperimentVariants.A) && (
            <div className={styles['pvr-cta-wrapper']}>
              <Button
                label={t(LOCALE_COMMON_PATHS.LISTING_CONTENT_TAKE_TOUR_BUTTON_LABEL, { defaultValue: 'Take a Tour' })}
                theme="primary"
                className={styles['pvr-cta-button']}
                onClick={() => openConnectionRequest(true)}
                testId={listingPageModalIds.takeTourBtn}
              />
            </div>
          )}

          {/* Show experiment buttons for Sold Listing */}
          {isMobile && soldListingExperimentId == ExperimentVariants.B && (
            <div className={styles['pvr-cta-wrapper']}>
              <Button
                label={t(LOCALE_COMMON_PATHS.LISTING_CONTENT_VIEW_SIMILAR_LISTINGS_FOR_SALE_BUTTON_LABEL, { defaultValue: 'View Similar Listings For Sale' })}
                theme="primary"
                className={styles['pvr-cta-button']}
                onClick={handleSoldSimilarListings}
              />
            </div>
          )}

          {isMobile && soldListingExperimentId == ExperimentVariants.C && (
            <div className={styles['pvr-cta-wrapper']}>
              <Button
                label={t(LOCALE_COMMON_PATHS.LISTING_CONTENT_GET_HOME_ESTIMATE_FOR_FREE_BUTTON_LABEL, { defaultValue: 'Get a Home Estimate for Free' })}
                theme="primary"
                className={styles['pvr-cta-button']}
                onClick={handleSoldExperimentClick}
              />
            </div>
          )}
        </>
      }
    </div>
  );
}

//#endregion

//#region Helper Functions

function getRepresentedByAgent(firstName: string | null, lastName: string | null, officeName: string | null, t: TFunction): string | null {
  if (!firstName && !lastName && !officeName) {
    return null;
  }

  const agentName = firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName;
  let context = undefined;
  if (agentName && officeName) {
    context = 'agent_and_office';
  } else if (agentName) {
    context = 'agent';
  } else if (officeName) {
    context = 'office';
  }

  return t(LOCALE_COMMON_PATHS.BUYER_REPRESENTATION_BUYER_REPRESENTED_BY, { agentName, officeName, context, defaultMessage: 'Buyer represented by ' });
}

function getEstimatedTimeRemainingOnMarket(zoopraisal: Zoopraisal, daysOnMarket: number, isAvailable: boolean, isRental: boolean, t: TFunction): string | null {
  if (zoopraisal && zoopraisal.averageDaysOnMarket && daysOnMarket) {
    const { averageDaysOnMarket } = zoopraisal;
    const timeRemaining = averageDaysOnMarket - daysOnMarket;
    if (timeRemaining <= 14 && timeRemaining > 0 && isAvailable && !isRental) {
      const timeRemainingVerbiage = timeRemaining > 0 ?
        t(LOCALE_COMMON_PATHS.PLURALS_UPPER_DAYS_WITH_COUNT,
          {
            count: timeRemaining,
            defaultValue: '{{count}} days',
          }
        )
        :
        t(LOCALE_COMMON_PATHS.ESTIMATED_TIME_REMAINING_ON_MARKET_VERY_SOON,
          {
            defaultValue: 'Very Soon',
          }
        );
      return timeRemainingVerbiage;
    }
  }
  return null;
}

//#endregion